/**
 * Advisor Insights Engine.
 *
 * Pure rule-based system that introspects the live game state and returns
 * a prioritised list of actionable strategic recommendations. Each rule is
 * a small function `(snapshot) => Insight | null`.
 *
 * An Insight looks like:
 *   {
 *     id:        'low_treasury',
 *     severity:  'critical' | 'warning' | 'info' | 'opportunity',
 *     category:  'finance' | 'fleet' | 'hr' | 'commercial' | 'fiscalite',
 *     title:     short headline (FR),
 *     body:      one to three sentences (FR) explaining WHAT and WHY,
 *     actions:   [{ label, to }]  optional CTAs that the panel renders
 *                                  as buttons linking to the relevant page,
 *     metric:    optional formatted side-figure (e.g. "Cash runway: 2 mois"),
 *     advisorTone: optional per-advisor variation
 *                  { maurice: '...sarcastic version...', camille: '...kind version...' }
 *   }
 *
 * The engine returns at most MAX_INSIGHTS items, sorted by severity then
 * by category. It's deterministic for a given snapshot — same inputs
 * always produce the same output.
 */
import { computeBalanceSheet } from './balanceSheet';
import { projectAnnualProfit } from './projectAnnualProfit';
import { estimateContractDailyToll } from './contractToll';
import { marketScaleFromState } from './marketScale';
import { RETAIL_ADVISOR_RULES } from './retailAdvisorRules';

const MAX_INSIGHTS = 8;
const SEVERITY_RANK = { critical: 0, warning: 1, opportunity: 2, info: 3 };

// ============================================================================
// === RULES ===
// Each rule is `(snapshot) => Insight | null`. Snapshot is the full game
// store state plus the precomputed balance sheet.
// ============================================================================

const ruleNegativeEquity = (s) => {
  if (s.balance.equity >= 0) return null;
  const minor = s.balance.actif.total > 0 && Math.abs(s.balance.equity) / s.balance.actif.total < 0.10;
  return {
    id: 'negative_equity',
    severity: minor ? 'warning' : 'critical',
    category: 'finance',
    title: minor ? 'Capitaux propres légèrement négatifs' : 'Capitaux propres négatifs',
    body: minor
      ? "Le passif dépasse l'actif d'un montant modeste (souvent dû à l'emprunt d'amorçage). Génère des contrats rentables et limite les nouveaux crédits le temps de revenir au-dessus de zéro."
      : "Votre passif dépasse votre actif. Sans recapitalisation rapide (cession d'actif, remboursement anticipé, ou hausse du résultat), vous risquez la cessation de paiement.",
    actions: [{ label: 'Voir le bilan détaillé', to: '/capitaux' }],
    metric: `Solvabilité ${s.balance.solvencyPct}%`,
    advisorTone: {
      maurice: minor
        ? "Vos capitaux propres sont en territoire négatif. Pas dramatique au démarrage, mais ne traînez pas — j'ai vu des entreprises mourir d'optimisme."
        : "Vos capitaux propres sont franchement négatifs. Vous ne pilotez plus une entreprise, vous gérez une faillite annoncée. Réagissez. Tout de suite.",
      camille: minor
        ? "Ne paniquez pas : c'est normal au tout début, l'emprunt initial pèse plus que le camion d'amorçage. Concentrez-vous sur le premier contrat rentable pour basculer en positif."
        : "Situation sérieuse. On va prioriser : 1) maximiser les revenus à court terme, 2) reporter tout achat non vital. Voyons ensemble votre bilan.",
    },
  };
};

const ruleCashRunwayLow = (s) => {
  const monthlyBurn = Math.max(1, s.monthlyExpenses || 0);
  const runwayMonths = Math.floor(s.treasury / monthlyBurn);
  if (runwayMonths >= 3) return null;
  return {
    id: 'cash_runway_low',
    severity: runwayMonths <= 1 ? 'critical' : 'warning',
    category: 'finance',
    title: `Trésorerie critique — ${runwayMonths} mois d'autonomie`,
    body: `Au rythme actuel des dépenses (${(monthlyBurn / 1000).toFixed(1)}k€/mois), votre trésorerie tient ${runwayMonths} mois. Visez 3 mois minimum pour absorber un imprévu (panne, mauvaise saison, audit).`,
    actions: [
      { label: 'Voir Banque & placements', to: '/banque' },
      { label: 'Décrocher un contrat', to: '/marche' },
    ],
    metric: `Cash runway ${runwayMonths} mois`,
    advisorTone: {
      maurice: `${runwayMonths} mois de trésorerie. Un peu juste pour faire le malin. Décrochez des contrats ou coupez les charges — à vous de choisir.`,
      camille: `Votre matelas de sécurité est court. On va rééquilibrer : un placement à court terme pour les liquidités dormantes, et de nouveaux contrats pour gonfler les rentrées.`,
    },
  };
};

const ruleHighDebtRatio = (s) => {
  if (s.balance.debtRatioPct <= 75) return null;
  return {
    id: 'high_debt_ratio',
    severity: s.balance.debtRatioPct > 100 ? 'critical' : 'warning',
    category: 'finance',
    title: `Endettement élevé — ${s.balance.debtRatioPct}% de l'actif`,
    body: "Au-delà de 60-70%, les banques deviennent réticentes à accorder de nouveaux crédits et durcissent leurs taux. Envisagez un remboursement anticipé sur le crédit le plus cher, ou cédez un actif non stratégique pour assainir le ratio.",
    actions: [{ label: 'Voir vos crédits', to: '/banque' }],
    metric: `Passif ${(s.balance.passif.total / 1000).toFixed(0)}k€`,
    advisorTone: {
      maurice: `${s.balance.debtRatioPct}% d'endettement. Les banques aiment vous voir venir, c'est normal — vous payez bien leurs intérêts. Allégez avant qu'elles ne vous coupent les vivres.`,
      camille: `Endettement à ${s.balance.debtRatioPct}%, c'est lourd. Identifions ensemble le crédit le plus cher : un remboursement anticipé peut faire baisser significativement vos charges mensuelles.`,
    },
  };
};

const ruleIdleVehicles = (s) => {
  const idle = (s.vehicles || []).filter(v => v.status === 'idle');
  if (idle.length < 2) return null;
  const total = s.vehicles.length;
  const idlePct = Math.round((idle.length / total) * 100);
  if (idlePct < 35) return null;
  return {
    id: 'fleet_underutilised',
    severity: idlePct >= 60 ? 'warning' : 'info',
    category: 'fleet',
    title: `Flotte sous-utilisée — ${idle.length}/${total} véhicules à l'arrêt`,
    body: "Chaque camion immobilisé coûte de l'entretien, du leasing ou de la décote sans générer de revenu. Décrochez plus de contrats, revendez l'excédent, ou redéployez vers une activité plus dynamique (Marché Phase suivante).",
    actions: [
      { label: 'Marché — démarcher', to: '/marche' },
      { label: 'Flotte', to: '/fleet' },
    ],
    metric: `${idlePct}% au repos`,
    advisorTone: {
      maurice: `${idle.length} camions qui prennent la poussière. Chaque jour qui passe, c'est de l'argent jeté. Bougez-vous.`,
      camille: `Votre flotte tourne à ${100 - idlePct}% — il reste de la marge. Soit on décroche plus de contrats, soit on aligne la flotte sur la demande réelle. Pas de fausse pudeur sur les reventes.`,
    },
  };
};

const ruleLowSatisfaction = (s) => {
  const drivers = s.drivers || [];
  if (drivers.length === 0) return null;
  const avgSat = Math.round(drivers.reduce((a, d) => a + (d.satisfaction || 0), 0) / drivers.length);
  if (avgSat >= 50) return null;
  const burnoutCount = drivers.filter(d => (d.satisfaction || 0) < 30).length;
  return {
    id: 'low_driver_satisfaction',
    severity: avgSat < 30 ? 'critical' : 'warning',
    category: 'hr',
    title: `Satisfaction chauffeurs basse — moyenne ${avgSat}%`,
    body: burnoutCount > 0
      ? `${burnoutCount} chauffeur·s en zone burnout (< 30%). Le risque de démission est élevé. Hausse de salaire, congés payés ou prime de fidélité peuvent rapidement renverser la tendance.`
      : "Une équipe moins motivée travaille plus lentement, accumule des retards et fatigue plus vite. Identifiez le ou les chauffeurs en bas de classement et investissez ciblé.",
    actions: [{ label: 'Voir l\'équipe RH', to: '/rh' }],
    metric: `Burnout: ${burnoutCount}`,
    advisorTone: {
      maurice: `Satisfaction moyenne à ${avgSat}%. Vos chauffeurs partent ou sabotent — l'un ou l'autre. À vous de choisir comment gérer.`,
      camille: `Vos chauffeurs envoient un signal clair. Avant que ça parte en démissions, voyons ensemble ce qui les fait tenir : salaire, ambiance, équilibre de vie ? Chaque cas est différent.`,
    },
  };
};

const ruleStaleContracts = (s) => {
  const market = s.activeContracts || [];
  if (market.length > 0) return null;
  if ((s.vehicles || []).length === 0) return null;
  if (s.totalDays < 5) return null;
  return {
    id: 'no_active_contracts',
    severity: 'warning',
    category: 'commercial',
    title: 'Aucun contrat actif',
    body: "Votre flotte est prête mais ne tourne pas. Allez démarcher de nouveaux clients dans le Marché : un contrat même petit vaut mieux qu'un camion à l'arrêt.",
    actions: [{ label: 'Aller au Marché', to: '/marche' }],
    advisorTone: {
      maurice: "Zéro contrat actif. Vos camions ne se vendront pas tout seuls. Allez sur le marché. Maintenant.",
      camille: "Pas de contrat en cours — c'est le moment de prospecter. Même un petit volume génère des références qui débloquent les contrats plus juteux ensuite.",
    },
  };
};

const ruleLowReputation = (s) => {
  if ((s.reputation || 0) >= 40) return null;
  return {
    id: 'low_reputation',
    severity: s.reputation < 25 ? 'warning' : 'info',
    category: 'commercial',
    title: `Réputation à ${Math.round(s.reputation)}/100`,
    body: "Une réputation < 40 ferme l'accès aux meilleurs contrats. Honorez tous les contrats en cours sans retard, évitez les pénalités, et envisagez un investissement marketing (Investissements) pour redorer l'image.",
    actions: [{ label: 'Investissements', to: '/finances' }],
    advisorTone: {
      maurice: "Réputation médiocre. Les bons donneurs d'ordre regardent ces chiffres avant tout. Tant que vous n'aurez pas remonté la pente, vous récupérerez les miettes.",
      camille: "La réputation se reconstruit avec de la régularité. Un contrat livré dans les temps + zéro pénalité = +1-2 points/mois. C'est lent mais sûr.",
    },
  };
};

const rulePlacementOpportunity = (s) => {
  const cash = s.treasury || 0;
  const monthlyBurn = Math.max(1, s.monthlyExpenses || 0);
  const runwayMonths = cash / monthlyBurn;
  // Need ≥ 6 months runway AND ≥ 30k€ idle cash AND no active placement.
  if (runwayMonths < 6) return null;
  if (cash < 30000) return null;
  if ((s.activePlacements || []).length > 0) return null;
  const dormantCash = Math.round(cash - monthlyBurn * 3);
  return {
    id: 'placement_opportunity',
    severity: 'opportunity',
    category: 'finance',
    title: `${(dormantCash / 1000).toFixed(0)}k€ dorment sur le compte courant`,
    body: "Au-delà de 3 mois de trésorerie de sécurité, le cash inactif perd de la valeur (inflation, coût d'opportunité). Placez 6-12 mois pour générer un revenu passif sans toucher au fonds de roulement.",
    actions: [{ label: 'Banque — placements', to: '/banque' }],
    metric: `Disponible: ${(dormantCash / 1000).toFixed(0)}k€`,
    advisorTone: {
      maurice: `${(dormantCash / 1000).toFixed(0)}k€ qui dorment sur le compte courant à 0%. C'est presque insultant. Placez-les.`,
      camille: `Excellente nouvelle : vous avez du cash en excès. Un placement 6 mois à ~2% rapporte environ ${Math.round((dormantCash * 0.02 / 2) / 100) * 100}€ — gratuit, sans risque, sans contrainte opérationnelle.`,
    },
  };
};

const ruleFleetExpansion = (s) => {
  const occRate = ((s.vehicles || []).filter(v => v.status === 'active').length) / Math.max(1, s.vehicles.length);
  // Need ≥ 80% occupation AND profitable last month AND positive equity AND ≥ 50k cash
  if (occRate < 0.8) return null;
  if ((s.lastDayRevenue || 0) - (s.lastDayExpenses || 0) <= 0) return null;
  if ((s.treasury || 0) < 50000) return null;
  if (s.balance.equity <= 0) return null;
  return {
    id: 'fleet_expansion',
    severity: 'opportunity',
    category: 'fleet',
    title: 'Votre flotte sature — pensez à investir',
    body: `Avec ${Math.round(occRate * 100)}% de taux d'occupation et un résultat positif, vous laissez peut-être passer du chiffre d'affaires. Évaluez l'ajout d'un camion (achat si trésorerie solide, leasing si besoin de souplesse).`,
    actions: [
      { label: 'Acheter / Louer', to: '/fleet' },
      { label: 'Capitaux & ratios', to: '/capitaux' },
    ],
    metric: `${Math.round(occRate * 100)}% occupation`,
    advisorTone: {
      maurice: "Votre flotte sature. Soit vous embauchez un camion, soit vous laissez les concurrents prendre les contrats. À vous de voir.",
      camille: "C'est le bon timing pour grandir : flotte qui sature + résultat positif + capitaux propres sains. Comparez achat (patrimoine) vs leasing (souplesse) avant de signer.",
    },
  };
};

const ruleNoTMS = (s) => {
  // Marge brute unlock = TMS + commercial. We don't have direct flags here,
  // so detect by absence of "marge_brute" in unlocked flags OR by phase ≥ 2.
  if (s.totalDays < 60) return null;
  if (s.phase < 2) return null;
  const unlocks = s.unlockedFlags || s.flags || {};
  if (unlocks.tms || unlocks.margeBrute) return null;
  // Skip if no DAF / no investment system in this game.
  return {
    id: 'install_tms',
    severity: 'info',
    category: 'finance',
    title: 'Aucun TMS — pilotage à l\'aveugle',
    body: "Sans Transport Management System, vous n'avez pas la marge brute par contrat. C'est la métrique #1 pour identifier les contrats qui détruisent de la valeur. L'investissement TMS s'amortit en 3-6 mois.",
    actions: [{ label: 'Investissements', to: '/finances' }],
    advisorTone: {
      maurice: "Pas de TMS ? Vous pilotez aux instruments d'avant-guerre. Installez-en un avant de me reparler de marges.",
      camille: "Un TMS débloque la marge brute par contrat — c'est ce qui transforme un transporteur en gestionnaire averti. Investissement modeste, ROI rapide.",
    },
  };
};

const ruleMortgageOpportunity = (s) => {
  // Suggest paying down mortgage if cash is comfortable AND a mortgage exists.
  const mortgageCredits = (s.credits || []).filter(c => c.mortgageVehicleId);
  if (mortgageCredits.length === 0) return null;
  const cash = s.treasury || 0;
  const monthlyBurn = Math.max(1, s.monthlyExpenses || 0);
  const runwayMonths = cash / monthlyBurn;
  if (runwayMonths < 9) return null;
  const cheapest = mortgageCredits.sort((a, b) => (b.annualRate || 0) - (a.annualRate || 0))[0];
  return {
    id: 'mortgage_early_repay',
    severity: 'opportunity',
    category: 'finance',
    title: 'Solde anticipé d\'une hypothèque possible',
    body: `Avec ${runwayMonths.toFixed(0)} mois de trésorerie, vous pouvez solder le crédit hypothéqué le plus cher (taux ${((cheapest.annualRate || 0) * 100).toFixed(2)}%) et libérer le camion. Économies d'intérêts + véhicule revendable à nouveau.`,
    actions: [{ label: 'Voir les crédits', to: '/banque' }],
    metric: `Capital restant: ${Math.round((cheapest.remainingPrincipal || 0) / 1000)}k€`,
    advisorTone: {
      maurice: `Vous avez du cash, et un crédit qui mange ${((cheapest.annualRate || 0) * 100).toFixed(2)}% par an. La maths est triviale.`,
      camille: `Belle opportunité de désendettement. Soldez le crédit le plus cher : vous gagnez en flexibilité (camion redevenu cessible) ET en charge financière mensuelle.`,
    },
  };
};

const ruleFiscalDeadline = (s) => {
  // Approximate fiscal cliff: high tax debt or IS provision growing fast.
  // 12/02/2026 — Aligné sur le barème PME (15 % puis 25 %) + lissage
  // début d'année via `projectAnnualProfit`, plutôt qu'un taux flat 25 %.
  const tvaPending = Math.max(0, (s.tvaCollectedTotal || 0) - (s.tvaDeductibleTotal || 0));
  const { annualIS: isProvision } = projectAnnualProfit(s);
  const taxDebt = tvaPending + isProvision;
  if (taxDebt < 5000) return null;
  if (taxDebt < s.treasury * 0.5) return null;
  return {
    id: 'fiscal_cliff',
    severity: 'warning',
    category: 'fiscalite',
    title: `Dette fiscale ${Math.round(taxDebt / 1000)}k€ — anticiper l'échéance`,
    body: `Vos impôts à venir (TVA ${Math.round(tvaPending / 1000)}k€ + provision IS ${Math.round(isProvision / 1000)}k€) représentent plus de la moitié de votre trésorerie. Provisionnez ce montant sur un compte dédié pour éviter une crise au moment du paiement.`,
    actions: [{ label: 'Voir Fiscalité', to: '/fiscalite' }],
    metric: `${Math.round((taxDebt / s.treasury) * 100)}% du cash`,
    advisorTone: {
      maurice: "Le fisc ne plaisante pas avec les retards. Provisionnez votre TVA et votre IS dès maintenant, ou préparez-vous à une crise de liquidité bien évitable.",
      camille: "L'impôt n'est pas une charge à débattre, c'est un cash flow différé. Mettre ce montant de côté sur un compte séparé évite les sueurs froides au moment de l'avis.",
    },
  };
};

// ── 18/06/2026 — Enrichissement pédagogique (évolutions récentes) ─────────
// Péages, montée en puissance du marché, couverture intérim des absences,
// crédit de TVA (CAPEX) et fatigue critique.

const ruleTollBurden = (s) => {
  const contracts = (s.activeContracts || []).filter(c => c.vehicleId);
  if (contracts.length === 0) return null;
  const vehById = Object.fromEntries((s.vehicles || []).map(v => [v.id, v]));
  const dailyToll = contracts.reduce((sum, c) => sum + estimateContractDailyToll(c, vehById[c.vehicleId]), 0);
  const dailyRevenue = contracts.reduce((sum, c) => sum + (c.dailyRevenue || 0), 0);
  if (dailyToll < 30 || dailyRevenue <= 0) return null;
  const pct = Math.round((dailyToll / dailyRevenue) * 100);
  if (pct < 8) return null;
  return {
    id: 'toll_burden',
    severity: pct >= 15 ? 'warning' : 'info',
    category: 'commercial',
    title: `Péages élevés — ${pct}% de votre CA quotidien`,
    body: `Vos contrats longue distance génèrent ${Math.round(dailyToll)} €/jour de péages (0,08 €/km au-delà de 30 km, plafonné 50 €/j/véhicule, ×1,5 pour les convois Tier 5). Vérifiez la marge NETTE de péage affichée sur chaque carte contrat avant de signer — un contrat lointain peut rapporter moins qu'un contrat proche.`,
    actions: [{ label: 'Voir mes contrats', to: '/' }],
    metric: `${Math.round(dailyToll)} €/j de péages`,
    advisorTone: {
      maurice: `${pct}% de votre chiffre part en péages. L'autoroute ne travaille pas pour vous — recalculez vos marges avant de signer le prochain contrat lointain.`,
      camille: `Les péages pèsent ${pct}% de votre CA quotidien. Ce n'est pas une fatalité : privilégiez les contrats proches de vos dépôts, ou vérifiez que le revenu/jour couvre bien le surcoût affiché sur la carte contrat.`,
    },
  };
};

const ruleMarketPressure = (s) => {
  const scale = marketScaleFromState(s);
  if ((scale.competitorScale || 1) < 1.15) return null;
  return {
    id: 'market_pressure',
    severity: 'info',
    category: 'commercial',
    title: `Concurrence renforcée — marché ×${scale.competitorScale.toFixed(2)}`,
    body: `Votre montée en puissance ne passe pas inaperçue : les concurrents grossissent avec vous (CA, flotte, effectifs ×${scale.competitorScale.toFixed(2)}) et les Appels d'Offres deviennent plus juteux (+${Math.round((scale.aoBonusBoost || 0) * 100)}% de prime). Les rachats coûtent plus cher, mais les AO récompensent mieux votre taille.`,
    actions: [
      { label: 'Voir le marché', to: '/marche' },
      { label: 'Appels d\'offres', to: '/tenders' },
    ],
    metric: `AO +${Math.round((scale.aoBonusBoost || 0) * 100)}%`,
    advisorTone: {
      maurice: `Le marché s'est réveillé : vos concurrents grossissent ×${scale.competitorScale.toFixed(2)}. Fini la promenade de santé — les AO valent le détour, les rachats coûtent un bras.`,
      camille: `C'est le signe d'une belle trajectoire : le marché s'adapte à votre taille. Concentrez-vous sur les Appels d'Offres — leur prime a augmenté avec votre croissance.`,
    },
  };
};

const ruleAbsenceCoverage = (s) => {
  const today = s.totalDays || 0;
  const vehById = Object.fromEntries((s.vehicles || []).map(v => [v.id, v]));
  const uncovered = (s.activeContracts || []).filter(c => {
    const d = (s.drivers || []).find(x => x.id === c.driverId);
    if (!d) return false;
    const isAway = ['sick', 'vacation', 'holiday', 'striking', 'training'].includes(d.status) || d.onHoliday === true;
    if (!isAway) return false;
    const v = vehById[c.vehicleId];
    const interim = v && (v.useInterim === true || (v.subcontracted === true && (v.subcontractUntil || 0) > today));
    return !interim;
  });
  if (uncovered.length === 0) return null;
  return {
    id: 'absence_uncovered',
    severity: 'warning',
    category: 'hr',
    title: `${uncovered.length} contrat${uncovered.length > 1 ? 's' : ''} à l'arrêt — chauffeur absent`,
    body: `Un chauffeur absent (arrêt, congés, formation) sans couverture = un contrat qui ne paye plus et des livraisons ratées. Deux leviers : activer un **intérimaire** (coût selon le tier du véhicule, revenus maintenus) ou réassigner un chauffeur disponible sur le véhicule.`,
    actions: [
      { label: 'Voir la flotte', to: '/fleet' },
      { label: 'Équipe RH', to: '/hr' },
    ],
    metric: `${uncovered.length} mission${uncovered.length > 1 ? 's' : ''} non couverte${uncovered.length > 1 ? 's' : ''}`,
    advisorTone: {
      maurice: `${uncovered.length} contrat(s) sans chauffeur au volant. Chaque jour d'arrêt, c'est du revenu perdu ET un client qui s'agace. L'intérim existe, servez-vous-en.`,
      camille: `Une absence non couverte n'est pas une catastrophe si on réagit vite : un intérimaire maintient le contrat pour un coût raisonnable, et votre chauffeur retrouvera sa mission à son retour.`,
    },
  };
};

const ruleTvaCredit = (s) => {
  const credit = (s.tvaDeductibleTotal || 0) - (s.tvaCollectedTotal || 0);
  if (credit < 1000) return null;
  return {
    id: 'tva_credit',
    severity: 'info',
    category: 'fiscalite',
    title: `Crédit de TVA en formation — ${Math.round(credit / 1000)}k€`,
    body: `Vos achats récents (véhicules, investissements) ont généré plus de TVA déductible que vous n'en avez collecté sur vos ventes. À la prochaine déclaration, ce **crédit de TVA** viendra réduire vos futurs versements — c'est l'effet trésorerie positif d'un investissement bien anticipé.`,
    actions: [{ label: 'Fiscalité — TVA', to: '/fiscalite' }],
    metric: `Crédit ${Math.round(credit / 1000)}k€`,
    advisorTone: {
      maurice: `Pour une fois, le fisc vous doit de l'argent : ${Math.round(credit / 1000)}k€ de crédit de TVA grâce à vos achats. Ne le dépensez pas deux fois — il s'impute sur les prochaines déclarations.`,
      camille: `Bonne nouvelle fiscale : la TVA sur vos investissements dépasse celle collectée. Ce crédit se reportera automatiquement — c'est un des mécanismes qui rendent l'investissement moins coûteux qu'il n'y paraît.`,
    },
  };
};

const ruleHighFatigue = (s) => {
  const tired = (s.drivers || []).filter(d => (d.fatigue || 0) > 75);
  if (tired.length === 0) return null;
  const critical = tired.filter(d => (d.fatigue || 0) > 90);
  return {
    id: 'high_fatigue',
    severity: critical.length > 0 ? 'critical' : 'warning',
    category: 'hr',
    title: `${tired.length} chauffeur${tired.length > 1 ? 's' : ''} en fatigue critique (> 75 %)`,
    body: `Au-delà de 75 % de fatigue, un chauffeur refuse de partir en mission et son risque d'accident grimpe. Anticipez : un **repos forcé** (2-3 jours) coûte moins cher qu'un arrêt maladie de 5 jours ou un accident. Pensez aussi à la rotation entre missions longues et courtes.`,
    actions: [{ label: 'Équipe RH', to: '/hr' }],
    metric: critical.length > 0 ? `${critical.length} au-delà de 90 %` : `${tired.length} > 75 %`,
    advisorTone: {
      maurice: `${tired.length} chauffeur(s) au bout du rouleau. Continuez comme ça et ce sera arrêt maladie, accident, ou démission — au choix. Le repos forcé, c'est maintenant.`,
      camille: `Vos chauffeurs tirent la langue. Un repos planifié aujourd'hui évite un arrêt subi demain — et un chauffeur reposé livre mieux, plus vite, sans casse.`,
    },
  };
};

// ============================================================================
// === ENGINE ===
// ============================================================================

const RULES = [
  ruleNegativeEquity,
  ruleCashRunwayLow,
  ruleHighDebtRatio,
  ruleIdleVehicles,
  ruleLowSatisfaction,
  ruleStaleContracts,
  ruleLowReputation,
  rulePlacementOpportunity,
  ruleFleetExpansion,
  ruleNoTMS,
  ruleMortgageOpportunity,
  ruleFiscalDeadline,
  // 18/06/2026 — enrichissement pédagogique (évolutions récentes).
  ruleTollBurden,
  ruleMarketPressure,
  ruleAbsenceCoverage,
  ruleTvaCredit,
  ruleHighFatigue,
  // SCD — règles retail (stock, marge, marketing, satisfaction, promos,
  // fidélité, e-commerce). Cf. utils/retailAdvisorRules.js.
  ...RETAIL_ADVISOR_RULES,
];

/**
 * Compute the list of strategic insights for the given game state.
 * @param {object} state - The full game store snapshot.
 * @returns {Insight[]} Ordered list (most critical first), capped to MAX_INSIGHTS.
 */
export function computeAdvisorInsights(state) {
  if (!state || !state.gameStarted) return [];

  const balance = computeBalanceSheet(state);
  const snapshot = { ...state, balance };

  const raw = RULES
    .map(rule => {
      try { return rule(snapshot); } catch { return null; }
    })
    .filter(Boolean);

  // Sort by severity rank, then by category alphabetical for stability.
  raw.sort((a, b) => {
    const sa = SEVERITY_RANK[a.severity] ?? 99;
    const sb = SEVERITY_RANK[b.severity] ?? 99;
    if (sa !== sb) return sa - sb;
    return (a.category || '').localeCompare(b.category || '');
  });

  return raw.slice(0, MAX_INSIGHTS);
}

/**
 * Helper to pick the right dialogue tone for an advisor.
 */
export function getInsightTone(insight, advisorId) {
  if (!insight) return null;
  const tone = insight.advisorTone || {};
  return tone[advisorId] || insight.body;
}
